import React, { useMemo, useState } from "react";
import { Alert, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import SearchableMultiSelectListCst from "@components/SearchableMultiSelectListCst";
import FiltersAccordionToggle from "@components/FiltersAccordionCst/FiltersAccordionToggle";
import InitiativeRowCst from "@components/InitiativeRowCst";
import { PagesID } from "@utils/utilsConfigurations";
import {
  InitiativeListItem,
  useInitiativesPaginatedList,
} from "@hooks/useInitiativesPaginatedList";
import { SelectedTargetItem } from "../../types";

const PAGE_SIZE = 10;

interface InitiativesTabProps {
  corporateId: string | number;
  selected: SelectedTargetItem[];
  onChange: (next: SelectedTargetItem[]) => void;
}

const InitiativesTab: React.FC<InitiativesTabProps> = ({
  corporateId,
  selected,
  onChange,
}) => {
  const { t } = useTranslation("enrollment-modal");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [fieldFilters, setFieldFilters] = useState<Record<string, any>>({});

  const { items, totalItems, totalPages, isLoading } =
    useInitiativesPaginatedList({
      corporateId,
      page,
      pageSize: PAGE_SIZE,
      search: search || undefined,
      fieldFilters,
    });

  const selectedSet = useMemo(
    () => new Set<string | number>(selected.map((s) => s.id)),
    [selected],
  );

  const handleToggle = (id: string | number, next: boolean) => {
    if (next) {
      const found = items.find((i) => String(i.id) === String(id));
      if (!found) return;
      onChange([...selected, { id: found.id, label: found.name, meta: found }]);
    } else {
      onChange(selected.filter((s) => String(s.id) !== String(id)));
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Alert severity="info" sx={{ borderRadius: "10px" }}>
        <strong>
          {t("targets.initiatives.banner.title") ||
            "Iscrizione di un'iniziativa ad un oggetto formativo"}
        </strong>
        <br />
        {t("targets.initiatives.banner.description") ||
          "Iscrivendo un'iniziativa ad un oggetto formativo tutte le persone presenti all'interno dell'iniziativa saranno iscritte all'oggetto."}
      </Alert>

      <SearchableMultiSelectListCst<InitiativeListItem>
        items={items}
        getId={(i) => i.id}
        renderRow={({ item }) => <InitiativeRowCst initiative={item} />}
        selectedIds={selectedSet}
        onToggle={handleToggle}
        initialSearchValue={search}
        onSearchSubmit={(v) => {
          setSearch(v);
          setPage(1);
        }}
        searchPlaceholder={
          t("targets.initiatives.search-placeholder") || "Nome"
        }
        searchButtonLabel={t("targets.search-button") || "Cerca"}
        hideSearch
        headerSlot={
          <FiltersAccordionToggle
            pageId={PagesID["corporate.initiative"]}
            value={fieldFilters}
            onApply={(next) => {
              setFieldFilters(next);
              setPage(1);
            }}
          />
        }
        page={page}
        pageSize={PAGE_SIZE}
        totalItems={totalItems}
        totalPages={totalPages}
        onPageChange={setPage}
        isLoading={isLoading}
        selectedCountLabel={
          t("targets.selected-count", { count: selected.length }) ||
          `${selected.length} elementi selezionati`
        }
        emptyLabel={
          t("targets.initiatives.empty") || "Nessuna iniziativa trovata"
        }
      />
    </Box>
  );
};

export default InitiativesTab;
